import CustomerDataBoardCard from 'customer-data-board-card';
import { useMemo } from 'react';
import { useEditBoardSettingsPanelContext } from 'crm-pipeline-board-settings-lib/EditBoardSettingsPanel';
import { useIsRecordInactive } from './hooks/useIsRecordInactive';
import { useIsRecordInCompleteColumn } from './hooks/useIsRecordInCompleteColumn';
import { useAssociationsFromObjectType } from '../associations/hooks/useAssociationsFromObjectType';
import { useTagPropertyWithValue } from './hooks/useTagPropertyWithValue';
import { useShouldShowAssociations } from './hooks/useShouldShowAssociations';
import { useCardProperties } from './hooks/useCardProperties';
import { useColumnType } from './hooks/useColumnType';
import { useSafePortalSetting } from './hooks/useSafePortalSetting';
import { getProperty } from 'customer-data-objects/objects/ObjectAccessors';
import { useLogger } from 'crm-object-board-core/logger';
import { useBoardSettings } from 'crm-object-board-core/frameworkAppSettings';
import { useGetCurrencyFormatting } from 'crm-object-board-core/multiCurrency';
import { useProperty } from 'crm-object-board-core/properties';
import { useGetHasGate } from 'crm-object-board-core/user';
import { useCardPerformance } from './hooks/useCardPerformance';
import { useTitleCardProperty } from './hooks/useCardTitleProperty';
import { useHasBulkActions } from '../bulkActions/hooks/useHasBulkActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CardComponent = cardData => {
  const hasBulkActions = useHasBulkActions();
  const associationsForCurrentType = useAssociationsFromObjectType();
  const {
    settings: cardPreferences
  } = useEditBoardSettingsPanelContext();
  const isCompact = cardPreferences.cozyCardPreferences.isCompact;
  const getCurrencyFormatting = useGetCurrencyFormatting();
  const boardSettings = useBoardSettings();
  const {
    hasGate
  } = useGetHasGate();
  const hasTooltipProperty = Boolean(boardSettings && boardSettings.cardTooltip);
  const descriptionProperty = useProperty(boardSettings && boardSettings.cardTooltip && boardSettings.cardTooltip.value.property || '');
  const priorityPropertValue = getProperty(cardData.data.object);
  const logger = useLogger();
  // card properties
  const secondaryProperties = useCardProperties();
  const titleProperty = useTitleCardProperty();

  // associations
  const showAssociations = useShouldShowAssociations();
  const primaryAssociationDefinition = associationsForCurrentType.get(boardSettings && boardSettings.cardAssociations && boardSettings.cardAssociations.value.primaryAssociationTypeId || '');
  const secondaryAssociationDefinition = associationsForCurrentType.get(boardSettings && boardSettings.cardAssociations && boardSettings.cardAssociations.value.secondaryAssociationTypeId || '');
  const associationsForCurrentTypeRecord = useMemo(() => {
    return Object.fromEntries(associationsForCurrentType);
  }, [associationsForCurrentType]);

  // next last activity
  const isNextLastActivityTurnedOn = !cardPreferences.nextLastActivityPreferences.isTurnedOff;
  const isInactiveCard = useIsRecordInactive({
    crmObject: cardData.data.object
  });
  const isInCompleteColumn = useIsRecordInCompleteColumn({
    crmObject: cardData.data.object
  });

  // tags
  const shouldShowTags = !cardPreferences.cozyCardPreferences.areTagsHidden;
  const tagPropertyWithValue = useTagPropertyWithValue({
    crmObject: cardData.data.object
  });
  const columnType = useColumnType(cardData.data.object);
  //tooltip property
  const tooltipProperty = hasTooltipProperty && descriptionProperty !== null ? descriptionProperty : undefined;
  const {
    data: isDealSplitData
  } = useSafePortalSetting({
    settingsKey: 'Forecasting:DealSplits:Enabled',
    defaultValue: 'false'
  });
  const isDealSplitsEnabled = useMemo(() => {
    return Boolean(isDealSplitData && isDealSplitData === 'true');
  }, [isDealSplitData]);
  const {
    cardRef,
    renderCardLoadedMarker
  } = useCardPerformance();
  const cardOptions = useMemo(() => {
    return {
      titleProperty: titleProperty || undefined,
      tags: shouldShowTags ? tagPropertyWithValue : undefined,
      secondaryProperties,
      compact: isCompact,
      showFooter: showAssociations,
      currencyFormatOptions: getCurrencyFormatting,
      getIsUngated: hasGate,
      isNextLastActivityTurnedOn,
      isInactive: !!isInactiveCard,
      isInColumnConsideredComplete: isInCompleteColumn,
      columnType,
      associationsForCurrentType: associationsForCurrentTypeRecord,
      primaryAssociationDefinition: primaryAssociationDefinition,
      // This casting is needed since there is a slight mismatch in the CDBC association definition type from our GQL type
      secondaryAssociationDefinition: secondaryAssociationDefinition,
      tooltipProperty,
      isDealSplitsEnabled,
      onNavigateToRecordPage() {
        var _cardData$data$object;
        logger.log({
          key: 'navigationToRecordsPage',
          metadata: {
            objectId: ((_cardData$data$object = cardData.data.object.objectId) === null || _cardData$data$object === void 0 ? void 0 : _cardData$data$object.toString()) || '',
            source: 'CARD',
            isInactive: !!isInactiveCard,
            dealPriority: priorityPropertValue
          }
        });
      },
      onTrackCardActions: (action, subAction) => {
        if (action === 'opened approval card popover') {
          logger.log({
            key: 'openedApprovalCardPopover',
            metadata: {
              action,
              subAction
            }
          });
        }
      }
    };
  }, [secondaryProperties, titleProperty, isCompact, isNextLastActivityTurnedOn, isInactiveCard, isInCompleteColumn, primaryAssociationDefinition, secondaryAssociationDefinition, associationsForCurrentTypeRecord, hasGate, showAssociations, tagPropertyWithValue, shouldShowTags, getCurrencyFormatting, columnType, tooltipProperty, isDealSplitsEnabled, logger, priorityPropertValue, cardData.data.object]);
  return /*#__PURE__*/_jsxs("div", {
    ref: cardRef,
    children: [renderCardLoadedMarker && renderCardLoadedMarker(), /*#__PURE__*/_jsx(CustomerDataBoardCard, {
      data: cardData.data,
      SelectionCheckbox: hasBulkActions ? cardData.SelectionCheckbox : undefined,
      options: cardOptions,
      boardBulkActionsVisible: hasBulkActions ? cardData.boardBulkActionsVisible : false
    })]
  });
};